import { useMemo, useState } from "react";
import { PlayCircle, Images } from "lucide-react";
import { useContent } from "../context/SiteContext";
import { mediaUrl, youtubeEmbed } from "../lib/api";
import { Container, PageHero, Empty, Loading } from "../components/site/Common";
import { Lightbox } from "../components/site/Lightbox";
import { ALBUM_CATEGORIES } from "../components/admin/resources";

const Thumb = ({ it }) => {
  if (it.media_type !== "video") return <img src={mediaUrl(it.thumb || it.url)} alt={it.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />;
  const yt = youtubeEmbed(it.url);
  return yt ? <img src={`https://img.youtube.com/vi/${yt.split("/").pop()}/hqdefault.jpg`} alt={it.title} className="w-full h-full object-cover" /> : <video src={mediaUrl(it.url)} className="w-full h-full object-cover" muted preload="metadata" />;
};

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${active ? "bg-blue-600 text-white" : "bg-white ring-1 ring-slate-200 text-slate-600 hover:bg-slate-100"}`} data-testid={testid}>{children}</button>
);

const AlbumCards = ({ albums, items, album, setAlbum }) => (
  <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 mb-12" data-testid="gallery-albums">
    {albums.map((a) => {
      const its = items.filter((g) => g.album_id === a.id);
      const cover = a.cover_image || its.find((x) => x.media_type === "photo")?.thumb || its.find((x) => x.media_type === "photo")?.url;
      return (
        <button key={a.id} onClick={() => setAlbum(album === a.id ? "All" : a.id)} className={`group text-left rounded-2xl overflow-hidden bg-white ring-2 transition-shadow ${album === a.id ? "ring-blue-600 shadow-lg" : "ring-transparent hover:shadow-md"}`} data-testid={`gallery-album-${a.id}`}>
          <div className="aspect-[16/10] bg-slate-200 relative overflow-hidden">
            {cover ? <img src={mediaUrl(cover)} alt={a.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <Images className="h-8 w-8 text-slate-400 absolute inset-0 m-auto" />}
            <span className="absolute bottom-2 right-2 rounded-full bg-[#0B1F44]/80 text-white text-xs px-2 py-0.5">{its.length}</span>
          </div>
          <div className="p-3">
            <p className="font-display font-semibold text-[#0B1F44] truncate">{a.title}</p>
            <p className="text-xs text-slate-500">{a.category}</p>
          </div>
        </button>
      );
    })}
  </div>
);

export default function Gallery() {
  const { items } = useContent("gallery");
  const { items: albums } = useContent("albums");
  const [type, setType] = useState("photo");
  const [cat, setCat] = useState("All");
  const [album, setAlbum] = useState("All");
  const [idx, setIdx] = useState(null);
  const titles = useMemo(() => Object.fromEntries((albums || []).map((a) => [a.id, a.title])), [albums]);
  const order = useMemo(() => Object.fromEntries((albums || []).map((a, i) => [a.id, i])), [albums]);
  const visibleAlbums = (albums || []).filter((a) => cat === "All" || a.category === cat);
  const list = useMemo(() => (items || [])
    .filter((g) => g.media_type === type && (cat === "All" || g.category === cat) && (album === "All" || g.album_id === album))
    .sort((a, b) => (order[a.album_id] ?? 999) - (order[b.album_id] ?? 999))
    .map((g) => ({ ...g, title: g.title || titles[g.album_id] || "" })), [items, type, cat, album, titles, order]);
  const current = albums?.find((a) => a.id === album);
  return (
    <>
      <PageHero eyebrow="Gallery" title="Photos & videos" text="Tournaments, events, training and association activities." />
      <section className="py-16 bg-slate-50 min-h-[50vh]">
        <Container>
          <div className="flex flex-col md:flex-row gap-4 justify-between mb-8">
            <div className="inline-flex rounded-full bg-white ring-1 ring-slate-200 p-1 self-start">
              {["photo", "video"].map((t) => <button key={t} onClick={() => setType(t)} className={`px-5 py-2 rounded-full text-sm font-semibold capitalize ${type === t ? "bg-[#0B1F44] text-white" : "text-slate-600"}`} data-testid={`gallery-type-${t}`}>{t}s</button>)}
            </div>
            <div className="flex flex-wrap gap-2">
              {["All", ...ALBUM_CATEGORIES].map((c) => <Chip key={c} active={cat === c} onClick={() => { setCat(c); setAlbum("All"); }} testid={`gallery-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</Chip>)}
            </div>
          </div>
          {albums && items && visibleAlbums.length > 0 && <AlbumCards albums={visibleAlbums} items={items} album={album} setAlbum={setAlbum} />}
          {current && (
            <div className="mb-8 flex flex-wrap items-end justify-between gap-3" data-testid="gallery-current-album">
              <div>
                <h2 className="font-display text-2xl font-bold text-[#0B1F44]">{current.title}</h2>
                {current.description && <p className="mt-1 text-slate-600 max-w-2xl">{current.description}</p>}
              </div>
              <Chip active={false} onClick={() => setAlbum("All")} testid="gallery-show-all">Show all albums</Chip>
            </div>
          )}
          {!items || !albums ? <Loading /> : list.length === 0 ? <Empty testid="gallery-empty">No {type}s here yet.</Empty> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gallery-grid">
              {list.map((g, i) => (
                <button key={g.id} onClick={() => setIdx(i)} className="group relative block w-full overflow-hidden rounded-2xl bg-slate-200 aspect-[4/3]" data-testid={`gallery-item-${g.id}`}>
                  <Thumb it={g} />
                  {g.media_type === "video" && <PlayCircle className="absolute inset-0 m-auto h-14 w-14 text-white drop-shadow-lg" />}
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B1F44]/90 to-transparent p-4 text-left opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="block text-xs text-blue-200 uppercase tracking-wider">{g.category}</span>
                    <span className="block text-sm text-white font-medium">{g.title}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </Container>
      </section>
      <Lightbox items={list} index={idx} onClose={() => setIdx(null)} onIndex={setIdx} />
    </>
  );
}
